import React, { useState } from 'react'
import { Row, Col } from 'react-flexbox-grid'
import moment from 'moment'
import { useTranslation } from 'react-i18next'

import { downloadFromUrlPromise } from 'helpers/downloadFromUrl'
import ContentBlock from 'components/ContentBlock'
import { Form, Select, DateTimePicker, Text } from 'components/forms'
import Button from 'components/Button'
import Divider from 'components/Divider'
import { WarningModal } from 'components/Modal'
import { useLazyQuery, useQuery } from 'components/graphql'
import { listShops } from 'gql/query'

import exportDatevQuery from '../../gql/exportDatev'
import downloadDatevQuery from '../../gql/downloadDatev'
import getAppConfig from '../../gql/getAppConfig'
import { resolveMultiShopIds } from './utils'

const Datev = () => {
	const [t] = useTranslation()

	const [formData, setFormData] = useState({
		shop: null,
		datePeriod: {
			from: moment().subtract(1, 'months').utc().startOf('month').toISOString(),
			to: moment().subtract(1, 'months').utc().endOf('month').toISOString()
		}
	})

	const [hasError, setHasError] = useState(false)
	const [warningMessages, setWarningMessages] = useState([])

	const { data: shopsData } = useQuery(listShops)
	const { data: appConfigData } = useQuery(getAppConfig, { fetchPolicy: 'cache-first' })

	const shopItems = shopsData?.listShops?.items || []
	const allShopsOption = shopItems.length > 0
		? { id: JSON.stringify(shopItems.map(o => o.id)), name: t('statistic_section.strings.all_shops') }
		: null
	const shopOptions = allShopsOption ? [allShopsOption, ...shopItems] : []



	const savedEmail = appConfigData?.getAppConfig?.cashRegisterGlobal?.savedEmail
	const effectiveEmail = formData.email ?? savedEmail ?? ''
	const effectiveShops = formData.abacusShops ?? (allShopsOption ? [allShopsOption] : [])

	const [sendDatev, { loading: isSendLoading }] = useLazyQuery(exportDatevQuery)
	const [fetchDownload, { loading: isDownloadLoading }] = useLazyQuery(downloadDatevQuery)

	const handleSend = async () => {
		const result = await sendDatev({
			variables: { shopId: resolveMultiShopIds(effectiveShops), email: effectiveEmail, datePeriod: formData.datePeriod }
		})
		if (!result?.data) {
			return
		}
		const { success, messages, error } = result.data.cashAssist.sendDatev
		if (error) {
			setHasError(true)
		}
		if (!success && messages?.length) {
			setWarningMessages(messages)
		}
	}

	const handleDownload = async () => {
		const result = await fetchDownload({
			variables: { shopId: resolveMultiShopIds(effectiveShops), datePeriod: formData.datePeriod }
		})
		if (!result?.data) {
			return
		}
		const { success, messages, error } = result.data.cashAssist.downloadDatev
		if (error) {
			setHasError(true)
		}
		if (!success && messages?.length) {
			setWarningMessages(messages)
		}
		if (success) {
			downloadFromUrlPromise(result.data.cashAssist.downloadDatev.link, `${effectiveShops.map(shop => shop.name).join(', ')}.csv`)
		}
	}

	// shop => setFormData(prev => ({ ...prev, shop }))
	const handleShopsChange = selectedOptions => {
		if (!selectedOptions || selectedOptions.length === 0) {
			setFormData(prev => ({ ...prev, abacusShops: allShopsOption ? [allShopsOption] : [] }))
			return
		}
		const lastSelected = selectedOptions[selectedOptions.length - 1]
		if (lastSelected.id === allShopsOption?.id) {
			setFormData(prev => ({ ...prev, abacusShops: [allShopsOption] }))
		} else {
			setFormData(prev => ({ ...prev, abacusShops: selectedOptions.filter(o => o.id !== allShopsOption?.id) }))
		}
	}

	return (
		<ContentBlock label='Datev'>
			{
				!!hasError && <WarningModal
					onClose={() => setHasError(false)}
					label={t('options_section.export.header')}
					text={`${t('common.strings.no_closings')}`}
				/>
			}
			{
				!!warningMessages?.length && <WarningModal
					onClose={() => setWarningMessages([])}
					label={t('options_section.export.header')}
					text={`${t('options_section.export.text', { items: warningMessages.join(', ') })}`}
				/>
			}
			<Form>
				<Row>
					<Col xs>
						<Select
							noForm
							value={effectiveShops}
							simpleValue={false}
							isMulti
							closeMenuOnSelect={false}
							label={t('common.strings.branch')}
							onChange={handleShopsChange}
							options={shopOptions}
							valueField='id'
							labelField='name'
							isSearchable
							validate={val => (Array.isArray(val) && val.length > 0) ? null : t('validators.required')}
							validateOnChange
						/>
						<Text
							noForm
							value={effectiveEmail}
							onChange={({ target }) => setFormData(prev => ({ ...prev, email: target.value }))}
							label={t('common.fields.email')}
							validate={val => val ? null : t('validators.required')}
							validateOnChange
						/>
					</Col>
					<Col xs>
						<DateTimePicker
							noForm
							value={formData.datePeriod}
							onChange={datePeriod => setFormData(prev => ({ ...prev, datePeriod }))}
							label={t('common.fields.date')}
							validate={val => val ? null : t('validators.required')}
							validateOnChange
							interval
						/>
					</Col>
				</Row>
				<Divider />
				<Button
					status={effectiveShops.length > 0 && effectiveEmail ? 'primary' : 'inactive'}
					text={t('message_section.strings.send')}
					loading={isSendLoading}
					onClick={handleSend}
				/>
				<Button
					status={effectiveShops.length > 0 ? 'primary' : 'inactive'}
					text={t('common.buttons.download')}
					loading={isDownloadLoading}
					onClick={handleDownload}
				/>
			</Form>
		</ContentBlock>
	)
}

export default Datev
